/**
 * Maps a saved `theme_config` (admin Theme Customizer → site_settings) to the
 * CSS custom properties the UI reads at runtime.
 *
 * The Theme Customizer only stores three base colors (primary_color,
 * accent_color, bg_warm), but the site consumes the full generated scale
 * (--color-sage-{50..900}, --color-terra{,-light,-dark}, --color-cream, etc.).
 * The public settings endpoint expands the base colors into `theme_config.generated`
 * (light) and `theme_config.generated.dark` (a complementary dark palette, derived
 * with the same generator the AI picker uses), so a live override matches a
 * generated palette exactly and the dark-mode toggle works.
 *
 * IMPORTANT: the override is emitted as a `<style>` block (`:root` for light,
 * `:root.dark` for dark) — NOT inline `documentElement.style` — because inline
 * styles on <html> outrank the `.dark` stylesheet rules and would clobber dark
 * mode. See buildThemeCss / PublicLayout.
 */

export interface GeneratedScale {
  primary?: Record<string, string>;
  /** Secondary/brown scale → `--color-brown-*` (full-scope palettes only). */
  secondary?: Record<string, string>;
  accent?: { base?: string; light?: string; dark?: string };
  warm?: { cream?: string; linen?: string; parchment?: string };
  /** Text neutrals → `--color-ink*` (+ legacy `--text-*`) (full-scope only). */
  text?: { primary?: string; secondary?: string; muted?: string };
  /** Background neutrals → `--color-surface*` (+ legacy `--bg-*`) (full-scope only).
   * `warm` is owned by the warm block (`--bg-warm`), so it is not mapped here. */
  bg?: { primary?: string; secondary?: string; tertiary?: string; warm?: string };
  /** Border/divider ramp → `--color-line*` (full-scope only). */
  border?: { line?: string; lineStrong?: string };
}

export interface GeneratedPalette extends GeneratedScale {
  /** Complementary dark-mode palette, applied under :root.dark. */
  dark?: GeneratedScale;
}

export interface ThemeConfigLike {
  primary_color?: string;
  accent_color?: string;
  bg_warm?: string;
  generated?: GeneratedPalette;
}

/** Convert a hex color string (#rrggbb) to "r, g, b" for use in rgba(). */
function hexToRgb(hex: string): string | null {
  const m = hex.match(/^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (!m) return null;
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

/** Map a generated scale (primary/accent/warm) to its CSS custom properties. */
function scaleToVars(scale: GeneratedScale | undefined): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!scale) return vars;

  if (scale.primary) {
    for (const [shade, value] of Object.entries(scale.primary)) {
      if (typeof value === 'string' && value) {
        vars[`--color-sage-${shade}`] = value;
        // Also emit the RGB components so theme-aware overlays/gradients
        // (e.g. .overlay-warm) track the live theme change.
        const rgb = hexToRgb(value);
        if (rgb) vars[`--color-sage-${shade}-rgb`] = rgb;
      }
    }
  }
  if (scale.secondary) {
    for (const [shade, value] of Object.entries(scale.secondary)) {
      if (typeof value === 'string' && value) vars[`--color-brown-${shade}`] = value;
    }
  }
  if (scale.accent) {
    if (scale.accent.base) vars['--color-terra'] = scale.accent.base;
    if (scale.accent.light) vars['--color-terra-light'] = scale.accent.light;
    if (scale.accent.dark) vars['--color-terra-dark'] = scale.accent.dark;
  }
  if (scale.warm) {
    if (scale.warm.cream) {
      vars['--color-cream'] = scale.warm.cream;
      vars['--bg-warm'] = scale.warm.cream;
    }
    if (scale.warm.linen) vars['--color-linen'] = scale.warm.linen;
    if (scale.warm.parchment) vars['--color-parchment'] = scale.warm.parchment;
  }
  if (scale.text) {
    // The storefront renders body text through the migrated `--color-ink*`
    // tokens (text-ink / text-ink-soft / text-ink-muted); the legacy `--text-*`
    // vars are still read by a couple of base rules (.prose-boutique, scrollbar),
    // so we set both for a coherent swap.
    if (scale.text.primary) { vars['--color-ink'] = scale.text.primary; vars['--text-primary'] = scale.text.primary; }
    if (scale.text.secondary) { vars['--color-ink-soft'] = scale.text.secondary; vars['--text-secondary'] = scale.text.secondary; }
    if (scale.text.muted) { vars['--color-ink-muted'] = scale.text.muted; vars['--text-muted'] = scale.text.muted; }
  }
  if (scale.bg) {
    // Surfaces render through `--color-surface*` (bg-surface / -soft / -muted);
    // legacy `--bg-*` kept for the scrollbar rule. `--bg-warm` is owned by the
    // warm block above, so bg.warm is intentionally not mapped here.
    if (scale.bg.primary) { vars['--color-surface'] = scale.bg.primary; vars['--bg-primary'] = scale.bg.primary; }
    if (scale.bg.secondary) { vars['--color-surface-soft'] = scale.bg.secondary; vars['--bg-secondary'] = scale.bg.secondary; }
    if (scale.bg.tertiary) { vars['--color-surface-muted'] = scale.bg.tertiary; vars['--bg-tertiary'] = scale.bg.tertiary; }
  }
  if (scale.border) {
    // Dividers/borders render through `--color-line*` (border-line / -strong).
    if (scale.border.line) vars['--color-line'] = scale.border.line;
    if (scale.border.lineStrong) vars['--color-line-strong'] = scale.border.lineStrong;
  }
  return vars;
}

/**
 * Light-mode color vars. Prefers the backend-expanded full palette
 * (theme_config.generated) and falls back to the three base colors — in which
 * case only the directly-mapped shades change, mirroring the legacy behavior.
 */
export function themeConfigColorVars(tc: ThemeConfigLike | undefined | null): Record<string, string> {
  if (!tc) return {};
  if (tc.generated) {
    const vars = scaleToVars(tc.generated);
    // Light-locked sage alias for the fixed-dark chrome (.surface-fixed-dark:
    // footer band + header strip). Mirror the LIGHT primary scale onto
    // --color-fixed-sage-* so those surfaces track a live theme swap — but ONLY
    // here, never in themeConfigDarkVars, so they keep the brand color in dark
    // mode instead of inverting. See web/src/index.css.
    if (tc.generated.primary) {
      for (const [shade, value] of Object.entries(tc.generated.primary)) {
        if (typeof value === 'string' && value) vars[`--color-fixed-sage-${shade}`] = value;
      }
    }
    return vars;
  }

  const vars: Record<string, string> = {};
  if (tc.primary_color) {
    vars['--color-sage-700'] = tc.primary_color;
    vars['--color-fixed-sage-700'] = tc.primary_color;
  }
  if (tc.accent_color) vars['--color-terra'] = tc.accent_color;
  if (tc.bg_warm) {
    vars['--color-cream'] = tc.bg_warm;
    vars['--bg-warm'] = tc.bg_warm;
  }
  return vars;
}

/** Dark-mode color vars (empty when no derived dark palette is present). */
export function themeConfigDarkVars(tc: ThemeConfigLike | undefined | null): Record<string, string> {
  return scaleToVars(tc?.generated?.dark);
}

/** Color to mirror onto <meta name="theme-color"> (mobile browser chrome). */
export function themeColorMeta(tc: ThemeConfigLike | undefined | null): string | null {
  if (!tc) return null;
  return tc.generated?.primary?.['700'] || tc.primary_color || null;
}

// localStorage key the pre-paint boot script reads to set <meta name="theme-color">
// before first paint. MUST stay in sync with the literal in
// web/public/theme-boot.js (a static asset that can't import this module).
// Not namespaced by SITE_ID on purpose: localStorage is origin-scoped and every
// prod tenant is its own domain, so there's no cross-tenant collision.
export const THEME_COLOR_CACHE_KEY = 'rumio:theme-color';

const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/;

/**
 * Cache the resolved theme-color so theme-boot.js can apply it before the next
 * load's first paint — closing the flash window where the build-time meta value
 * shows while PublicLayout waits on the settings request. Validates the value is
 * a hex color so only a safe value is ever persisted, and swallows storage
 * errors (private mode / blocked storage).
 */
export function cacheThemeColor(color: string | null | undefined): void {
  if (!color || !HEX_COLOR.test(color)) return;
  try {
    localStorage.setItem(THEME_COLOR_CACHE_KEY, color);
  } catch {
    /* storage unavailable — boot script keeps the baked value */
  }
}

// A CSS custom-property value that breaks out of its `key: value;` declaration
// (or the surrounding `:root { … }` block) would let a saved theme inject
// arbitrary rules into the injected <style> for every visitor. Legitimate color
// and font values never contain these characters, so we drop any value that
// does — defense-in-depth at the render boundary, independent of input
// validation on whichever route wrote the theme_config.
const CSS_VALUE_BREAKOUT = /[;{}<>\\]/;

function varsToDecls(vars: Record<string, string>): string {
  return Object.entries(vars)
    .filter(([, v]) => !CSS_VALUE_BREAKOUT.test(v))
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}

/**
 * Build the CSS text for the injected <style> override: light vars (+ any extra
 * root vars such as fonts) under `:root`, dark vars under `:root.dark`. Returns
 * '' when there's nothing to apply. Appended to <head> so it wins over the
 * build-time theme by cascade order; `:root.dark` outranks `:root` so the dark
 * toggle correctly switches.
 */
export function buildThemeCss(
  tc: ThemeConfigLike | undefined | null,
  extraRootVars: Record<string, string> = {},
): string {
  const light = { ...themeConfigColorVars(tc), ...extraRootVars };
  const dark = themeConfigDarkVars(tc);

  let css = '';
  if (Object.keys(light).length) css += `:root { ${varsToDecls(light)} }\n`;
  if (Object.keys(dark).length) css += `:root.dark { ${varsToDecls(dark)} }\n`;
  return css;
}
